/**
 * She is one character, not up to a dozen `<GlitimeMark>` instances each
 * blinking on their own clock. Every mounted mark files a claim here for the
 * station it lives in; exactly one claim is ever "awake", and the rest go
 * dark without unmounting, so nothing in the layout jumps as she moves.
 *
 * Rank decides the winner, newest breaks a tie at the same rank — two persona
 * toasts stacked, the later one lit — and claim ids are handed out by a
 * monotonic counter, so "newest" is just "highest id".
 */

import { useEffect, useState } from 'react'
import { create } from 'zustand'

export type Station = 'stage' | 'zone' | 'toast' | 'busy' | 'sync' | 'strip'

export interface PresenceClaim {
  id: number
  station: Station
}

const RANK: Record<Station, number> = {
  stage: 60,
  zone: 50,
  toast: 40,
  busy: 30,
  sync: 20,
  strip: 10,
}

interface PresenceRegistryState {
  claims: PresenceClaim[]
}

export const usePresenceRegistry = create<PresenceRegistryState>(() => ({ claims: [] }))

let nextId = 1

/** Files a claim for `station` and returns its id — pass it to `releasePresence` later. */
export function claimPresence(station: Station): number {
  const id = nextId++
  usePresenceRegistry.setState((state) => ({ claims: [...state.claims, { id, station }] }))
  return id
}

export function releasePresence(id: number): void {
  usePresenceRegistry.setState((state) => ({
    claims: state.claims.filter((claim) => claim.id !== id),
  }))
}

/** Highest rank wins; among equal ranks, the higher (newer) id does. Null claims list. */
export function awakeClaimId(claims: PresenceClaim[]): number | null {
  let winner: PresenceClaim | null = null
  for (const claim of claims) {
    if (
      winner == null ||
      RANK[claim.station] > RANK[winner.station] ||
      (RANK[claim.station] === RANK[winner.station] && claim.id > winner.id)
    ) {
      winner = claim
    }
  }
  return winner?.id ?? null
}

/**
 * Claims `station` for as long as this component is mounted, and reports
 * whether this particular claim is the one currently awake.
 *
 * The claim id lives in local state rather than a ref: under StrictMode's
 * mount → cleanup → mount, each effect run makes its own id and releases
 * exactly the one it made, and `setId` inside the effect keeps this render in
 * step with the store instead of reading a ref that a later run already
 * moved on from.
 *
 * `station` is nullable so a mark that never coordinates — the login screen,
 * the dev lab — can still call this unconditionally and always come back
 * awake.
 */
export function usePresenceClaim(station: Station | null | undefined): boolean {
  const [id, setId] = useState<number | null>(null)
  const claims = usePresenceRegistry((state) => state.claims)

  useEffect(() => {
    if (station == null) return
    const claimId = claimPresence(station)
    setId(claimId)
    return () => {
      setId(null)
      releasePresence(claimId)
    }
  }, [station])

  if (station == null) return true
  return id != null && awakeClaimId(claims) === id
}
